import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { api, type ArrangementRow } from "../api.js";
import type { JSX } from "react";

const columnHelper = createColumnHelper<ArrangementRow>();

const COLUMNS = [
  columnHelper.accessor("arrangementRef", { header: "Arrangement Ref" }),
  columnHelper.accessor("providerCode", { header: "Provider Code" }),
  columnHelper.accessor("functionRef", { header: "Function Ref" }),
  columnHelper.accessor("startDate", { header: "Start Date", cell: (i) => i.getValue() ?? "" }),
  columnHelper.accessor("endDate", { header: "End Date", cell: (i) => i.getValue() ?? "" }),
  columnHelper.accessor("annualCostEur", {
    header: "Annual Cost (EUR)",
    cell: (i) => {
      const v = i.getValue();
      return v !== null ? v.toLocaleString("en-EU", { maximumFractionDigits: 0 }) : "";
    },
  }),
  columnHelper.accessor("provenance", { header: "Provenance" }),
];

interface Props {
  registerId: string;
}

export function ArrangementsGrid({ registerId }: Props): JSX.Element {
  const [data, setData] = useState<ArrangementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15_000);

    api
      .listArrangements(registerId)
      .then((rows) => {
        setData(rows);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) {
          setError("Request timed out — check your connection and retry.");
        } else {
          setError((e instanceof Error ? e.message : String(e)) || "Failed to load arrangements.");
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeoutId);
    };
  }, [registerId, retryCount]);

  const table = useReactTable({
    data,
    columns: COLUMNS,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  if (loading) {
    return (
      <div className="loading-state">
        <span className="spinner" aria-hidden="true" />
        Loading arrangements…
      </div>
    );
  }

  if (error !== null) {
    return (
      <div className="error" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <span>{error}</span>
        <button onClick={() => setRetryCount((c) => c + 1)} style={{ flexShrink: 0 }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div data-testid="arrangements-grid">
      <input
        className="filter-input"
        type="search"
        placeholder="Filter..."
        value={globalFilter}
        onChange={(e) => setGlobalFilter(e.target.value)}
      />
      <div className="workspace-grid" style={{ overflowX: "auto" }}>
        <table>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={
                      header.column.getCanSort() ? () => header.column.toggleSorting() : undefined
                    }
                    style={{ cursor: header.column.getCanSort() ? "pointer" : "default" }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getIsSorted() === "asc"
                      ? " ↑"
                      : header.column.getIsSorted() === "desc"
                        ? " ↓"
                        : ""}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length}
                  style={{ textAlign: "center", padding: "2rem", color: "var(--muted)" }}
                >
                  No arrangements. Use Import tab to upload data.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: "0.5rem", fontSize: "0.8rem" }}>
        {table.getRowModel().rows.length} of {data.length} rows
      </p>
    </div>
  );
}
